export type MeasurementMode = 'direct' | 'orthogonal'
export type MeasurementSource = 'grid' | 'entry' | 'pathway'

export interface MeasurementPoint {
  x: number
  y: number
  source: MeasurementSource
  id?: string
}

export interface Measurement {
  mode: MeasurementMode
  points: MeasurementPoint[]
}

export function appendMeasurementPoint(measurement: Measurement, point: MeasurementPoint): MeasurementPoint[] {
  const previous = measurement.points[measurement.points.length - 1]
  if (measurement.mode !== 'orthogonal' || !previous || previous.x === point.x || previous.y === point.y) {
    return [...measurement.points, point]
  }
  return [
    ...measurement.points,
    { x: point.x, y: previous.y, source: 'grid' },
    point,
  ]
}

export function calculateMeasurementLength(measurement: Measurement): number {
  return measurement.points.slice(1).reduce((total, point, index) => {
    const previous = measurement.points[index]
    const dx = point.x - previous.x
    const dy = point.y - previous.y
    return total + (measurement.mode === 'orthogonal' ? Math.abs(dx) + Math.abs(dy) : Math.hypot(dx, dy))
  }, 0)
}

export function snapMeasurementPoint(
  point: { x: number; y: number },
  grid: { tilePx: number; originX: number; originY: number },
  candidates: MeasurementPoint[] = [],
  threshold = 18,
): MeasurementPoint {
  const nearest = candidates
    .map(candidate => ({ candidate, distance: Math.hypot(point.x - candidate.x, point.y - candidate.y) }))
    .filter(item => item.distance <= threshold)
    .sort((a, b) => a.distance - b.distance)[0]
  if (nearest) return { ...nearest.candidate }

  return {
    x: grid.originX + Math.round((point.x - grid.originX) / grid.tilePx) * grid.tilePx,
    y: grid.originY + Math.round((point.y - grid.originY) / grid.tilePx) * grid.tilePx,
    source: 'grid',
  }
}

export function encodeMeasurement(measurement: Measurement): string {
  return `${measurement.mode}|${measurement.points.map(point => `${Math.round(point.x)},${Math.round(point.y)}`).join(';')}`
}

export function decodeMeasurement(value: string | null): Measurement | null {
  if (!value) return null
  const [mode, rawPoints] = value.split('|')
  if (mode !== 'direct' && mode !== 'orthogonal') return null
  type DecodedPoint = { x: number; y: number; source: 'grid' }
  const points: Array<DecodedPoint | null> = (rawPoints || '').split(';').map(raw => {
    const [x, y] = raw.split(',').map(Number)
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y, source: 'grid' as const } : null
  })
  if (points.some(point => point === null)) return null
  const safePoints = points.filter((point): point is DecodedPoint => point !== null)
  if (safePoints.length < 2 || safePoints.length > 64) return null
  return { mode, points: safePoints }
}

export function formatMeasurementLength(px: number): string {
  return `${(px / 100).toFixed(2)} м`
}
